import axios from 'axios'
import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { config } from '../../function/config'
import { toast } from 'react-toastify'
import AdminHeader from './AdminHeader'
import {
    FaArrowLeft,
    FaHeart,
    FaInfoCircle,
    FaMapMarkerAlt,
    FaUser,
} from 'react-icons/fa'

export default function NewAdmissionOverview() {
    const { id } = useParams()
    const navigate = useNavigate()
    const token = localStorage.getItem('token')

    const [newAdmission, setNewAdmission] = useState(null)
    const [loading, setLoading] = useState(false)

    const fetchNewAdmission = async () => {
        try {
            setLoading(true)

            const res = await axios.get(
                `${config.baseurl}get_new_admission/${id}/`,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Token ${token}`
                    }
                }
            )

            console.log('New Admission Response:', res.data)

            if (res.status === 200) {
                const admissionData = res.data.data

                if (admissionData) {
                    setNewAdmission(admissionData)
                } else {
                    toast.error('New Admission not found')
                }
            } else {
                toast.error('Failed to fetch New Admission details')
            }

        } catch (error) {
            console.error(
                'Error:',
                error.response?.data || error
            )

            const message =
                error.response?.data?.message ||
                error.response?.data?.error ||
                'Failed to fetch New Admission details'

            toast.error(message)

        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        if (id) {
            fetchNewAdmission()
        }
    }, [id])

    /* =========================
       LOADING
    ========================= */

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 ml-64 5xl:ml-0">

                <AdminHeader />

                <div className="flex h-[70vh] items-center justify-center">

                    <div className="text-center">

                        <div className="mx-auto mb-4 h-9 w-9 animate-spin rounded-full border-4 border-slate-200 border-t-blue-600"></div>

                        <p className="text-sm font-medium text-slate-500">
                            Loading admission details...
                        </p>

                    </div>

                </div>

            </div>
        )
    }

    /* =========================
       NOT FOUND
    ========================= */

    if (!newAdmission) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 ml-64 5xl:ml-0">
                <AdminHeader />

                <div className="flex h-[70vh] items-center justify-center">
                    <div className="text-center">
                        <p className="text-sm font-medium text-slate-500">
                            New Admission details not found.
                        </p>

                        <button
                            onClick={() => navigate(-1)}
                            className="mt-5 inline-flex items-center gap-2 rounded-xl bg-[#000435] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[#000326]"
                        >
                            <FaArrowLeft />
                            Go Back
                        </button>
                    </div>
                </div>
            </div>
        )
    }

    const isOrphan = newAdmission.orphan_status === true

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 ml-64 5xl:ml-0">
            <AdminHeader />

            {/* HEADER */}
            <div className="mx-auto max-w-7xl px-6 pt-16 pb-6">
                <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <h1 className="mt-1 text-3xl font-bold text-slate-800">
                            {newAdmission.first_name}{' '}
                            {newAdmission.last_name}
                        </h1>

                        <p className="mt-2 text-sm text-slate-500">
                            View registered beneficiary information and admission details.
                        </p>
                    </div>

                    <button
                        onClick={() => navigate(-1)}
                        className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-5 py-3 text-sm font-semibold text-slate-700 shadow-sm ring-1 ring-slate-200 transition hover:bg-slate-50"
                    >
                        <FaArrowLeft />
                        Back
                    </button>
                </div>
            </div>

            {/* MAIN CONTENT */}
            <div className="mx-auto max-w-7xl px-6 pb-12">
                <div className="grid grid-cols-2 gap-6 4xl:grid-cols-1">

                    {/* PERSONAL INFORMATION */}
                    <InfoCard
                        title="Personal Information"
                        subtitle="Basic beneficiary details"
                        icon={<FaUser />}
                    >
                        <div className="grid grid-cols-2 gap-4">
                            <InfoItem
                                label="First Name"
                                value={newAdmission.first_name}
                            />

                            <InfoItem
                                label="Last Name"
                                value={newAdmission.last_name}
                            />

                            <InfoItem
                                label="Age"
                                value={newAdmission.age}
                            />

                            <InfoItem
                                label="Date of Birth"
                                value={newAdmission.dob}
                            />

                            <InfoItem
                                label="Gender"
                                value={newAdmission.gender}
                            />

                            <InfoItem
                                label="Blood Group"
                                value={newAdmission.blood_group}
                            />
                        </div>
                    </InfoCard>


                    {/* ADMISSION INFORMATION */}
                    <InfoCard
                        title="Admission Information"
                        subtitle="Admission and beneficiary classification"
                        icon={<FaHeart />}
                    >

                        <div className="grid grid-cols-1 gap-4">
                            <InfoItem
                                label="Category"
                                value={newAdmission.category}
                            />

                            <InfoItem
                                label="Orphan Status"
                                value={
                                    isOrphan
                                        ? 'Orphan'
                                        : 'Not Orphan'
                                }
                            />

                            <InfoItem
                                label="Admission Status"
                                value={
                                    newAdmission.is_active
                                        ? 'Active'
                                        : 'Inactive'
                                }
                                status={true}
                                active={newAdmission.is_active}
                            />
                        </div>
                    </InfoCard>

                    {/* GUARDIAN & ADDRESS ONLY FOR NOT ORPHAN */}
                    {!isOrphan && (
                        <InfoCard
                            title="Guardian & Address"
                            subtitle="Guardian and residential information"
                            icon={<FaMapMarkerAlt />}
                        >

                            <div className="space-y-4">
                                <InfoItem
                                    label="Guardian Details"
                                    value={newAdmission.guardian_details}
                                />

                                <InfoItem
                                    label="Address"
                                    value={newAdmission.address}
                                />
                            </div>
                        </InfoCard>
                    )}


                    {/* ADDITIONAL INFORMATION */}
                    <InfoCard
                        title="Additional Information"
                        subtitle="Other beneficiary information"
                        icon={<FaInfoCircle />}
                    >

                        <div className="space-y-4">
                            <InfoItem
                                label="Identification Mark"
                                value={newAdmission.identification_mark}
                            />

                            <InfoItem
                                label="Remarks"
                                value={newAdmission.remarks}
                            />
                        </div>
                    </InfoCard>
                </div>
            </div>
        </div>
    )
}


/* ==========================================
   INFO CARD COMPONENT
========================================== */

function InfoCard({title, subtitle, icon, children}) {
    return (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">

            {/* CARD HEADER */}

            <div className="flex items-center gap-3 border-b border-slate-200 px-6 py-5">

                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                    {icon}
                </div>

                <div>

                    <h2 className="text-base font-bold text-slate-800">
                        {title}
                    </h2>

                    <p className="mt-0.5 text-xs text-slate-400">
                        {subtitle}
                    </p>

                </div>

            </div>

            {/* CARD BODY */}

            <div className="p-6">
                {children}
            </div>

        </div>
    )
}


/* ==========================================
   INFO ITEM COMPONENT
========================================== */

function InfoItem({label, value, status = false, active = false}) {
    return (
        <div className="rounded-xl bg-slate-50 px-4 py-3">

            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                {label}
            </p>

            {status ? (

                <div className="mt-2">

                    <span
                        className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${
                            active
                                ? 'bg-green-100 text-green-700'
                                : 'bg-slate-200 text-slate-600'
                        }`}
                    >
                        <span
                            className={`mr-2 h-2 w-2 rounded-full ${
                                active
                                    ? 'bg-green-500'
                                    : 'bg-slate-500'
                            }`}
                        ></span>

                        {value}

                    </span>

                </div>

            ) : (

                <p className="mt-1.5 break-words text-sm font-semibold text-slate-800">
                    {value !== null &&
                    value !== undefined &&
                    value !== ''
                        ? value
                        : '-'}
                </p>

            )}

        </div>
    )
}